import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useMatches,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { buildHead } from "../lib/seo";
import { getSiteData } from "../lib/site-data.functions";
import { Header } from "../components/itv/Header";
import { Footer } from "../components/itv/Footer";
import { JsonLd } from "../components/seo/JsonLd";
import { physicianSchema, localBusinessSchema } from "../lib/schema";
import { cssDosTokens } from "../lib/admin/tokens-marca";
import { AgendamentoProvider } from "../lib/itv/agendamento";
import { WhatsAppFlutuante } from "../components/itv/WhatsAppFlutuante";
import { GTM_ID } from "../lib/itv/gtm";

/**
 * Snippet oficial do Google Tag Manager: inicializa o dataLayer já no <head>
 * e carrega o gtm.js na primeira interação ou, sem interação, no mínimo 5 s
 * depois do evento load, quando o navegador estiver ocioso. O rastreamento
 * continua completo: o dataLayer guarda tudo o que acontece antes e o GTM
 * processa a fila ao iniciar.
 * O guarda `w["_gtm_"+i]` evita injeção duplicada em navegações internas.
 */
function snippetGtm(gtmId: string): string {
  const id = JSON.stringify(gtmId);
  return `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});if(w['_gtm_'+i]){return;}w['_gtm_'+i]=1;var c=function(){if(w['_gtmL_'+i]){return;}w['_gtmL_'+i]=1;var f=d.getElementsByTagName(s)[0],j=d.createElement(s);j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+encodeURIComponent(i);f.parentNode.insertBefore(j,f);};w.__carregarGtm=c;var e=['pointerdown','keydown','touchstart','scroll'];var h=function(){e.forEach(function(n){w.removeEventListener(n,h)});c()};e.forEach(function(n){w.addEventListener(n,h,{once:true,passive:true})});var a=function(){setTimeout(function(){if('requestIdleCallback'in w){w.requestIdleCallback(c,{timeout:2000})}else{c()}},4000)};if(d.readyState==='complete'){a()}else{w.addEventListener('load',a,{once:true})};d.addEventListener('click',function(ev){var g=ev.target&&ev.target.closest?ev.target.closest('a[href*="tintim.link"]'):null;if(!g)return;var u=g.href;if(!u)return;var navegou=false;var ir=function(){if(navegou){return;}navegou=true;w.location.href=u;};var mod=(ev.ctrlKey||ev.metaKey||ev.shiftKey||ev.button===1||g.target==='_blank');var item={event:'clique_agendar',cta_url:u,cta_texto:(g.textContent||'').replace(/\\s+/g,' ').trim(),cta_local:g.getAttribute('data-cta')||(typeof g.className==='string'?g.className:'')};if(mod){item.eventTimeout=700;w[l].push(item);return;}ev.preventDefault();if(w.__carregarGtm){w.__carregarGtm();}item.eventCallback=ir;item.eventTimeout=700;w[l].push(item);setTimeout(ir,800);},true);})(window,document,'script','dataLayer',${id});`;
}


/**
 * Marca explícita da rota: `declaraPhysician: true` quer dizer que a própria
 * rota emite o Physician dela. Uma entidade, uma declaração por página, então
 * o layout raiz não emite o reduzido nesse caso. Nada é deduzido pela URL.
 */
declare module "@tanstack/react-router" {
  interface StaticDataRouteOption {
    declaraPhysician?: boolean;
    /** A rota emite o próprio consultório (MedicalClinic); o raiz não repete. */
    declaraClinica?: boolean;
  }
}


function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: unknown; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error as Error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  // Todo o SEO da página (title, description, canonical, Open Graph e robots)
  // sai de page_seo pelo buildHead. Nada de tag fixa escrita aqui.
  loader: async ({ location }) => {
    if (location.pathname.startsWith("/admin")) {
      return {
        meta: [{ name: "robots", content: "noindex, nofollow" }],
        links: [],
        site: null,
        path: location.pathname,
      };
    }
    const [head, site] = await Promise.all([
      buildHead({
        data: {
          path: location.pathname,
          fallback: { title: "Dr. Thiago Viana" },
        },
      }),
      getSiteData(),
    ]);
    return { ...head, site, path: location.pathname };
  },
  head: ({ loaderData }) => {
    // Instalação canônica do GTM: snippet no <head>, direto no HTML do
    // servidor. O painel nunca carrega o GTM.
    const gtmId = GTM_ID;
    const mostraGtm =
      !!gtmId && !loaderData?.path?.startsWith("/admin");
    return {
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      ...(loaderData?.meta ?? []),
    ],
    scripts: mostraGtm && gtmId
      ? [{ type: "text/javascript", children: snippetGtm(gtmId) }]
      : [],
    links: [
      ...(loaderData?.links ?? []),
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "icon", href: "/favicon.png", type: "image/png" },
      ...(mostraGtm
        ? [
            { rel: "preconnect", href: "https://www.googletagmanager.com" },
            { rel: "dns-prefetch", href: "https://www.googletagmanager.com" },
          ]
        : []),
      // Fontes acima da dobra, servidas do próprio site.

      {
        rel: "preload",
        href: "/fonts/jost-latin.woff2",
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        href: "/fonts/newsreader-latin.woff2",
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous",
      },
    ],
    };
  },
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="pt-BR">
      <head>
        <script dangerouslySetInnerHTML={{ __html: `(function(){
          var key='site-module-reload';
          function recover(error){
            var message=String(error&&error.message||error||'');
            if(!/Importing a module script failed|Failed to fetch dynamically imported module|error loading dynamically imported module/i.test(message))return;
            var previous=Number(sessionStorage.getItem(key)||0);
            if(Date.now()-previous>60000){
              sessionStorage.setItem(key,String(Date.now()));
              location.reload();
              return;
            }
            if(document.getElementById('module-recovery'))return;
            var show=function(){
              if(document.getElementById('module-recovery'))return;
              var box=document.createElement('div');
              box.id='module-recovery';
              box.setAttribute('role','alert');
              box.innerHTML='<p>Não foi possível carregar esta página.</p><a href="'+location.pathname+'">Tentar novamente</a>';
              document.body.replaceChildren(box);
            };
            if(document.body)show();else document.addEventListener('DOMContentLoaded',show,{once:true});
          }
          window.addEventListener('vite:preloadError',function(event){event.preventDefault();recover(event.payload)});
          window.addEventListener('unhandledrejection',function(event){recover(event.reason)});
        })();` }} />
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const { site } = Route.useLoaderData();
  const rotaDeclaraPhysician = useMatches({
    select: (matches) => matches.some((match) => match.staticData?.declaraPhysician === true),
  });
  const rotaDeclaraClinica = useMatches({
    select: (matches) => matches.some((match) => match.staticData?.declaraClinica === true),
  });
  // O painel e as landing pages não usam o layout do site: sem cabeçalho,
  // rodapé ou dados estruturados do layout raiz. O GTM carrega no site e nas
  // landing pages, mas nunca no painel.
  const caminho = useRouterState({
    select: (estado) => estado.location.pathname,
  });
  const ehAdmin = caminho.startsWith("/admin");
  const painel =
    ehAdmin ||
    caminho === "/lp" ||
    caminho.startsWith("/lp/") ||
    caminho === "/links" ||
    caminho.startsWith("/links/");

  // Instalação canônica do GTM: o <noscript> com o iframe de fallback fica
  // dentro do <body>, logo no início da árvore. O painel nunca carrega o GTM.
  const gtmNoScript = !ehAdmin && GTM_ID
    ? (
      <noscript>
        <iframe
          src={`https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(GTM_ID)}`}
          height="0"
          width="0"
          style={{ display: "none", visibility: "hidden" }}
          title="Google Tag Manager"
        />
      </noscript>
    )
    : null;

  if (painel) {
    return (
      <QueryClientProvider client={queryClient}>
        <div className="min-h-screen bg-background">
          {gtmNoScript}
          <Outlet />
        </div>
      </QueryClientProvider>
    );
  }

  // Marca gravada no painel. Sai no HTML do servidor, antes da primeira pintura.
  const cssMarca = cssDosTokens(site?.tokens ?? {});

  return (
    <QueryClientProvider client={queryClient}>
      {gtmNoScript}
      {cssMarca ? <style dangerouslySetInnerHTML={{ __html: cssMarca }} /> : null}
      <AgendamentoProvider link={site?.appointmentUrl ?? null}>
        <div className="flex min-h-screen flex-col bg-background-white">
          <Header />
          <div className="flex-1">
            {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
            <Outlet />
          </div>
          <Footer />
          <WhatsAppFlutuante />
          {/* Padrão do site: versão reduzida, com o que o rodapé mostra em tela.
              A rota manda: quando ela declara o próprio Physician, o padrão sai. */}
          {rotaDeclaraPhysician ? null : (
            <JsonLd data={physicianSchema(site?.doctor ?? null, "reduzida")} />
          )}
          {rotaDeclaraClinica ? null : <JsonLd data={localBusinessSchema(site?.clinic ?? null)} />}
        </div>
      </AgendamentoProvider>
    </QueryClientProvider>
  );
}

